import type { FuseNavItemType } from "@fuse/core/FuseNavigation/types/FuseNavItemType";

type AppRouteItem = {
	path: string;
	url: string;
	tag?: string;
};

type AppRouteGroup = {
	base?: string;
	[key: string]: string | AppRouteItem | AppRouteGroup | undefined;
};

type AppRoutes = Record<string, AppRouteGroup>;

// ----------------------------------------------------------- HELPERS --------------------------------------------
function montarPath(...partes: string[]): string {
	return partes.join("/");
}

function montarUrl(...partes: string[]): string {
	return `/${montarPath(...partes)}`;
}

const APP_ROOT = "apps";

/**
 * Definições de rotas para os módulos da aplicação, organizadas por funcionalidade.
 * Cada módulo tem uma rota base e rotas específicas para suas funcionalidades.
 * Convencao de tags: "<modulo>.<subgrupo>.<tela>" (minusculo, sem separador no segmento).
 */
const ROTAS_COMPRAS = {
	base: montarPath(APP_ROOT, "compras"),
	compraInteligente: "compra-inteligente",
	prePedido: "pre-pedido",
	notasFiscaisEntrada: "notas-fiscais-entrada",
} as const;

const ROTAS_VENDAS = {
	base: montarPath(APP_ROOT, "vendas"),
	emissaoFiscal: "emissao-fiscal",
} as const;

const ROTAS_ANALISES = {
	base: montarPath(APP_ROOT, "analises"),
	relatorios: "relatorios",
	dashboard: "dashboard",
} as const;

const ROTAS_GESTAO = {
	base: montarPath(APP_ROOT, "gestao"),
	powerbi: "powerbi",
} as const;

const ROTAS_FINANCEIRO = {
	base: montarPath(APP_ROOT, "financeiro"),
	contasPagar: "contas-pagar",
	titulosPagar: "titulos-pagar",
	requisicoesDespesas: "requisicoes-despesas",
	assinaturaEletronica: "assinatura-eletronica",
} as const;

const ROTAS_CONFIG = {
	base: montarPath(APP_ROOT, "config"),
	conta: "conta",
} as const;

const ROTAS_CONFIG_GERAL = {
	base: montarPath(APP_ROOT, "configuracoes-gerais"),
	restricoes: "restricoes",
	integracaoIA: "integracao-ia",
} as const;

const ROTAS_PRODUTO = {
	base: montarPath(APP_ROOT, "produtos"),
	produtos: "cadastro-produtos/:produtoId?",
} as const;

// =============================== ESTRUTURA DE ROTAS NOMEADAS (CONSTANTES) =====================================
/**
 * Estrutura de rotas nomeadas da aplicação.
 *
 * Este objeto é usado para referenciar caminhos e URLs de forma consistente
 * em toda a aplicação.
 */
export const APP_ROUTES = {
	produtos: {
		base: ROTAS_PRODUTO.base,
		cadastroProdutos: {
			path: ROTAS_PRODUTO.produtos,
			url: montarUrl(ROTAS_PRODUTO.base, "cadastro-produtos"),
			tag: "produtos.cadastro-produtos",
		},
	},
	config: {
		base: ROTAS_CONFIG.base,
		conta: {
			path: ROTAS_CONFIG.conta,
			url: montarUrl(ROTAS_CONFIG.base, ROTAS_CONFIG.conta),
		},
	},
	configGeral: {
		base: ROTAS_CONFIG_GERAL.base,
		restricoes: {
			path: ROTAS_CONFIG_GERAL.restricoes,
			url: montarUrl(ROTAS_CONFIG_GERAL.base, ROTAS_CONFIG_GERAL.restricoes),
			tag: "seguranca.restricoes",
		},
		integracaoIA: {
			path: ROTAS_CONFIG_GERAL.integracaoIA,
			url: montarUrl(ROTAS_CONFIG_GERAL.base, ROTAS_CONFIG_GERAL.integracaoIA),
			tag: "config_geral.integracao_ia",
		},
	},
	compras: {
		base: ROTAS_COMPRAS.base,
		compraInteligente: {
			path: ROTAS_COMPRAS.compraInteligente,
			url: montarUrl(ROTAS_COMPRAS.base, ROTAS_COMPRAS.compraInteligente),
			tag: "compra.inteligente",
		},
		prePedido: {
			path: ROTAS_COMPRAS.prePedido,
			url: montarUrl(ROTAS_COMPRAS.base, ROTAS_COMPRAS.prePedido),
			tag: "compra.prepedido",
		},
		notasFiscaisEntrada: {
			path: ROTAS_COMPRAS.notasFiscaisEntrada,
			url: montarUrl(ROTAS_COMPRAS.base, ROTAS_COMPRAS.notasFiscaisEntrada),
			tag: "compra.notas-fiscais-entrada",
		},
	},
	vendas: {
		base: ROTAS_VENDAS.base,
		emissaoFiscal: {
			path: ROTAS_VENDAS.emissaoFiscal,
			url: montarUrl(ROTAS_VENDAS.base, ROTAS_VENDAS.emissaoFiscal),
			tag: "vendas.emissao-fiscal",
		},
	},
	analises: {
		base: ROTAS_ANALISES.base,
		relatorio: {
			path: ROTAS_ANALISES.relatorios,
			url: montarUrl(ROTAS_ANALISES.base, ROTAS_ANALISES.relatorios),
			tag: "analises.relatorios",
		},
		dashboard: {
			path: ROTAS_ANALISES.dashboard,
			url: montarUrl(ROTAS_ANALISES.base, ROTAS_ANALISES.dashboard),
			tag: "analises.dashboard.contas-a-pagar",
		},
	},
	gestao: {
		base: ROTAS_GESTAO.base,
		powerbi: {
			path: ROTAS_GESTAO.powerbi,
			url: montarUrl(ROTAS_GESTAO.base, ROTAS_GESTAO.powerbi),
			tag: "gestao.powerbi",
		},
	},
	financeiro: {
		base: ROTAS_FINANCEIRO.base,
		contasPagar: {
			path: ROTAS_FINANCEIRO.contasPagar,
			url: montarUrl(ROTAS_FINANCEIRO.base, ROTAS_FINANCEIRO.contasPagar),
			tag: "financeiro.contaspagar",
		},
		contasPagarTitulos: {
			path: ROTAS_FINANCEIRO.titulosPagar,
			url: montarUrl(ROTAS_FINANCEIRO.base, ROTAS_FINANCEIRO.contasPagar, ROTAS_FINANCEIRO.titulosPagar),
			tag: "financeiro.contaspagar.titulospagar",
		},
		assistentePagamentos: {
			path: "financeiro/contas-a-pagar/assistente-pagamentos",
			url: "/financeiro/contas-a-pagar/assistente-pagamentos",
			tag: "financeiro.contaspagar.assistente-pagamentos",
		},
		contasPagarRequisicoes: {
			path: ROTAS_FINANCEIRO.requisicoesDespesas,
			url: montarUrl(ROTAS_FINANCEIRO.base, ROTAS_FINANCEIRO.contasPagar, ROTAS_FINANCEIRO.requisicoesDespesas),
			tag: "financeiro.contaspagar.requisicao-despesas",
		},
		assinaturaEletronica: {
			path: ROTAS_FINANCEIRO.assinaturaEletronica,
			url: montarUrl(ROTAS_FINANCEIRO.base, ROTAS_FINANCEIRO.assinaturaEletronica),
			tag: "financeiro.assinatura-eletronica",
		},
	},
} as const satisfies AppRoutes;

// ================================================== NAVEGAÇÃO DO APLICATIVO (SIDEBAR) ==========================
/**
 * Itens de navegação do painel de menu da aplicação. Arvore de navegação que define a estrutura do menu lateral, incluindo títulos, ícones e hierarquia de itens.
 */
export const APP_NAVIGATION: FuseNavItemType[] = [
	{
		id: "produtos",
		title: "Produtos",
		type: "collapse",
		icon: "lucide:clipboard-list",
		children: [
			{
				id: "produtos.cadastro-produtos",
				title: "Cadastro de Produtos",
				type: "item",
				url: APP_ROUTES.produtos.cadastroProdutos.url,
				tag: APP_ROUTES.produtos.cadastroProdutos.tag,
			},
		],
	},
	{
		id: "compras",
		title: "Compras",
		type: "collapse",
		icon: "lucide:shopping-cart",
		children: [
			{
				id: "compras.compra-inteligente",
				title: "Compra Inteligente",
				type: "item",
				url: APP_ROUTES.compras.compraInteligente.url,
				tag: APP_ROUTES.compras.compraInteligente.tag,
			},
			{
				id: "compras.pre-pedido",
				title: "Pré Pedido",
				type: "item",
				url: APP_ROUTES.compras.prePedido.url,
				tag: APP_ROUTES.compras.prePedido.tag,
			},
			{
				id: "compras.notas-fiscais-entrada",
				title: "Notas Fiscais de Entrada",
				type: "item",
				url: APP_ROUTES.compras.notasFiscaisEntrada.url,
				tag: APP_ROUTES.compras.notasFiscaisEntrada.tag,
			},
		],
	},
	{
		id: "vendas",
		title: "Vendas",
		type: "collapse",
		icon: "lucide:shopping-bag",
		children: [
			{
				id: "vendas.emissao-fiscal",
				title: "Emissão Fiscal",
				type: "item",
				url: APP_ROUTES.vendas.emissaoFiscal.url,
				tag: APP_ROUTES.vendas.emissaoFiscal.tag,
			},
		],
	},
	{
		id: "analises",
		title: "Análises",
		type: "collapse",
		icon: "lucide:file-text",
		children: [
			{
				id: "analises.inicio",
				title: "Relatórios",
				type: "item",
				url: APP_ROUTES.analises.relatorio.url,
				tag: APP_ROUTES.analises.relatorio.tag,
			},
			{
				id: "analises.dashboard",
				title: "Dashboard",
				type: "collapse",
				children: [
					{
						id: "analises.dashboard.contas-a-pagar",
						title: "Contas a Pagar",
						type: "item",
						url: APP_ROUTES.analises.dashboard.url,
						tag: APP_ROUTES.analises.dashboard.tag,
					},
				],
			},
		],
	},
	{
		id: "gestao",
		title: "Gestão",
		type: "collapse",
		icon: "lucide:folder-kanban",
		children: [
			{
				id: "gestao.powerbi",
				title: "Power BI",
				type: "item",
				url: APP_ROUTES.gestao.powerbi.url,
				tag: APP_ROUTES.gestao.powerbi.tag,
			},
		],
	},
	{
		id: "financeiro",
		title: "Financeiro",
		type: "collapse",
		icon: "lucide:dollar-sign",
		children: [
			{
				id: "financeiro.contas-pagar",
				title: "Contas A Pagar",
				type: "collapse",
				tag: APP_ROUTES.financeiro.contasPagar.tag,
				children: [
					{
						id: "financeiro.contas-pagar.titulos",
						title: "Títulos Pagar",
						type: "item",
						url: APP_ROUTES.financeiro.contasPagarTitulos.url,
						tag: APP_ROUTES.financeiro.contasPagarTitulos.tag,
					},
					{
						id: "financeiro.contas-pagar.assistente-pagamentos",
						title: "Assistente de Pagamentos",
						type: "item",
						url: APP_ROUTES.financeiro.assistentePagamentos.url,
						tag: APP_ROUTES.financeiro.assistentePagamentos.tag,
					},
					{
						id: "financeiro.contas-pagar.requisicoes-despesas",
						title: "Requisições de Despesas",
						type: "item",
						url: APP_ROUTES.financeiro.contasPagarRequisicoes.url,
						tag: APP_ROUTES.financeiro.contasPagarRequisicoes.tag,
					},
				],
			},
			{
				id: "financeiro.assinatura-eletronica",
				title: "Assinatura Eletrônica",
				type: "item",
				url: APP_ROUTES.financeiro.assinaturaEletronica.url,
				tag: APP_ROUTES.financeiro.assinaturaEletronica.tag,
			},
		],
	},
	{
		id: "config_geral",
		title: "Configurações Gerais",
		type: "collapse",
		icon: "lucide:shield-check",
		children: [
			{
				id: "config_geral.restricoes",
				title: "Restrições",
				type: "item",
				url: APP_ROUTES.configGeral.restricoes.url,
				tag: APP_ROUTES.configGeral.restricoes.tag,
			},
			{
				id: "config_geral.integracao_ia",
				title: "Integração IA",
				type: "item",
				url: APP_ROUTES.configGeral.integracaoIA.url,
				tag: APP_ROUTES.configGeral.integracaoIA.tag,
			},
		],
	},
];
